// Resolve an unknown outcome: the request to Defra may or may not have been
// recorded and Defra has no lookup for us, so an admin checks Defra's own service
// and tells us what they found. A note is mandatory; "Received" also needs the
// Waste Tracking ID read from Defra's service. Nothing is sent from here.

import { useEffect, useState } from 'react';
import { Modal } from '../../../components/ui/Modal/Modal';
import { Button } from '../../../components/ui/Button/Button';
import { resolveUnknownOutcome } from '../../../api/dwt';
import { useToast } from '../../../hooks/useToast';
import styles from '../dwt.module.css';

export const RESOLVE_GUIDANCE =
  'Defra has no lookup for us. Check the Report receipt of waste service to see whether the movement exists before choosing.';

const ID_RE = /^[A-Za-z0-9-]{4,40}$/;

export interface ResolveUnknownDialogProps {
  open: boolean;
  receipt: { receipt_enc_id: string; unique_id: string } | null;
  onClose: () => void;
  onResolved: () => void;
}

export function ResolveUnknownDialog({ open, receipt, onClose, onResolved }: ResolveUnknownDialogProps): JSX.Element {
  const toast = useToast();
  const [choice, setChoice] = useState<'received' | 'not_received' | ''>('');
  const [wtid, setWtid] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setChoice('');
      setWtid('');
      setNote('');
      setBusy(false);
    }
  }, [open, receipt?.receipt_enc_id]);

  const noteOk = note.trim().length >= 5;
  const idOk = ID_RE.test(wtid.trim());
  const canSave = choice !== '' && noteOk && (choice === 'not_received' || idOk);

  async function save() {
    if (receipt === null || choice === '' || !canSave) return;
    setBusy(true);
    try {
      await resolveUnknownOutcome(
        receipt.receipt_enc_id,
        choice === 'received'
          ? { resolution: 'received', waste_tracking_id: wtid.trim(), note: note.trim() }
          : { resolution: 'not_received', note: note.trim() },
      );
      toast.success(
        choice === 'received'
          ? 'Recorded as received by Defra.'
          : 'Recorded as not received. The receipt can now be sent again.',
      );
      onResolved();
      onClose();
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } } | null)?.response?.data?.message;
      toast.error(msg ?? 'Could not save. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open && receipt !== null}
      onClose={onClose}
      title={`Resolve unknown outcome${receipt ? `: ${receipt.unique_id}` : ''}`}
      size="md"
      preventClose={busy}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={() => void save()} loading={busy} disabled={!canSave}>
            Save
          </Button>
        </>
      }
    >
      <div className={styles.stack}>
        <p role="note" style={{ margin: 0 }}>
          {RESOLVE_GUIDANCE}
        </p>
        <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
          <legend className={styles.muted}>What did you find?</legend>
          <label className={styles.check}>
            <input type="radio" name="resolution" checked={choice === 'received'} onChange={() => setChoice('received')} />
            Received: the movement is in Defra’s service
          </label>
          <br />
          <label className={styles.check}>
            <input type="radio" name="resolution" checked={choice === 'not_received'} onChange={() => setChoice('not_received')} />
            Not received: the movement is not in Defra’s service
          </label>
        </fieldset>

        {choice === 'received' ? (
          <div className={styles.field}>
            <label htmlFor="resolve-wtid">Waste Tracking ID (as shown in Defra’s service)</label>
            <input id="resolve-wtid" value={wtid} onChange={(e) => setWtid(e.target.value)} autoComplete="off" />
            {wtid.trim() !== '' && !idOk ? (
              <span className={styles.fieldError}>Enter the Waste Tracking ID exactly as Defra shows it.</span>
            ) : null}
          </div>
        ) : null}
        {choice === 'not_received' ? (
          <p className={styles.muted} style={{ margin: 0 }}>
            The receipt will be allowed to go to Defra again (it is not sent automatically). Only choose this if you are sure the
            movement is not there, otherwise it will be reported twice.
          </p>
        ) : null}

        <div className={styles.field}>
          <label htmlFor="resolve-note">Note (required)</label>
          <textarea
            id="resolve-note"
            rows={3}
            maxLength={500}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="What you checked, for the audit trail"
          />
          {note.trim() !== '' && !noteOk ? <span className={styles.fieldError}>Enter at least 5 characters.</span> : null}
        </div>
      </div>
    </Modal>
  );
}
